import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import { Box, Modal, useMediaQuery } from "@mui/material";
import { ThemeProvider, alpha } from "@mui/material/styles";

import { darkTheme } from "@/src/theme/theme";

import { AdditionalInfoBadge } from "@/src/components/atoms/AdditionalInfoBadge";
import CarouselArrow from "@/src/components/atoms/CarouselArrow";
import { LightboxCloseButton } from "@/src/components/atoms/LightboxCloseButton";
import { LightboxCounter } from "@/src/components/atoms/LightboxCounter";
import { LightboxRotateButton } from "@/src/components/atoms/LightboxRotateButton";
import { LightboxImage } from "@/src/components/molecules/LightboxImage";
import LightboxThumbnails from "@/src/components/molecules/LightboxThumbnails";

import { ImageLoadStatusMap } from "@/src/hooks/useProgressiveImageLoader";
import { UnifiedCarouselImage } from "@/src/types/carousel";

interface UnifiedLightboxProps {
  images: UnifiedCarouselImage[];
  open: boolean;
  startIndex: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  showCounter?: boolean;
  counterPosition?: "top" | "bottom";
  showThumbnails?: boolean;
  showRotateButton?: boolean;
  zoomEnabled?: boolean;
  zoomFactor?: number;
  lensSize?: number;
  renderImage?: (image: UnifiedCarouselImage, index: number) => ReactNode;
  imageLoadStatus?: ImageLoadStatusMap;
  onPriorityLoadOriginal?: (image: UnifiedCarouselImage) => Promise<void>;
  showAdditionalInfoBadge?: boolean;
}

/** Near-opaque black chip so the white chevrons stay legible over any image. */
const arrowSx = {
  backgroundColor: alpha(darkTheme.palette.common.black, 0.87),
  color: darkTheme.palette.common.white,
  "&:hover": {
    backgroundColor: darkTheme.palette.common.black
  }
};

const UnifiedLightbox = (props: UnifiedLightboxProps) => {
  const {
    images,
    open,
    startIndex,
    onClose,
    onIndexChange,
    showCounter = true,
    counterPosition = "bottom",
    showThumbnails = false,
    showRotateButton = false,
    zoomEnabled = false,
    zoomFactor = 2,
    lensSize = 200,
    renderImage,
    imageLoadStatus,
    onPriorityLoadOriginal,
    showAdditionalInfoBadge = false
  } = props;

  // The lightbox is a full-screen viewer over a black backdrop, so it stays on
  // the dark palette even while the rest of the app renders in light mode.
  const isMobile = useMediaQuery(darkTheme.breakpoints.down("sm"));
  const currentIndex = startIndex;
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const outerContainerRef = useRef<HTMLDivElement | null>(null);
  const [rotation, setRotation] = useState(0);
  const [isImageSwitching, setIsImageSwitching] = useState(false);

  const handlePrevious = useCallback(() => {
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    onIndexChange(newIndex);
  }, [currentIndex, images.length, onIndexChange]);

  const handleNext = useCallback(() => {
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    onIndexChange(newIndex);
  }, [currentIndex, images.length, onIndexChange]);

  useEffect(() => {
    setRotation(0);
    setIsImageSwitching(true);
    const timer = setTimeout(() => {
      setIsImageSwitching(false);
    }, 50);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  useEffect(() => {
    if (!open) {
      setRotation(0);
    }
  }, [open]);

  // Priority load original image when lightbox opens or navigates (desktop only)
  useEffect(() => {
    if (!open || isMobile) return;

    const currentImage = images[currentIndex];
    if (currentImage && onPriorityLoadOriginal) {
      onPriorityLoadOriginal(currentImage);
    }
  }, [open, currentIndex, images, isMobile, onPriorityLoadOriginal]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;

      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowLeft") {
        handlePrevious();
      } else if (event.key === "ArrowRight") {
        handleNext();
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => {
      globalThis.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handlePrevious, handleNext, onClose]);

  const handleRotate = () => {
    setIsImageSwitching(false);
    setRotation((prev) => prev + 90);
  };

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    // Close modal if clicking on backdrop (outer container) but not on image container
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];

  const renderContent = () => {
    if (renderImage && currentImage) {
      return (
        <Box
          sx={{
            transform: `rotate(${rotation}deg)`,
            transition: isImageSwitching ? "none" : "transform 0.3s ease",
            maxWidth: "100%",
            maxHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none"
          }}
        >
          {renderImage(currentImage, currentIndex)}
        </Box>
      );
    }

    if (currentImage) {
      return (
        <LightboxImage
          image={currentImage}
          index={currentIndex}
          zoomEnabled={zoomEnabled}
          isMobile={isMobile}
          zoomFactor={zoomFactor}
          lensSize={lensSize}
          rotation={rotation}
          isImageSwitching={isImageSwitching}
          imageLoadStatus={imageLoadStatus}
        />
      );
    }
    return null;
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Modal
        open={open}
        onClose={onClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1300,
          overflow: "hidden"
        }}
        aria-labelledby="lightbox-title"
        aria-describedby="lightbox-description"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: darkTheme.palette.common.black
            }
          }
        }}
      >
        <Box
          ref={outerContainerRef}
          onClick={handleBackdropClick}
          sx={{
            position: "relative",
            width: "100vw",
            height: "100dvh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
            WebkitTapHighlightColor: "transparent",
            overflow: "hidden",
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none"
          }}
        >
          {images.length > 1 && !isMobile && (
            <>
              <CarouselArrow
                direction="left"
                onClick={handlePrevious}
                isVisible={true}
                iconSize={36}
                sx={{
                  ...arrowSx,
                  width: 48,
                  height: 96,
                  borderRadius: "0 8px 8px 0"
                }}
              />
              <CarouselArrow
                direction="right"
                onClick={handleNext}
                isVisible={true}
                iconSize={36}
                sx={{
                  ...arrowSx,
                  width: 48,
                  height: 96,
                  borderRadius: "8px 0 0 8px"
                }}
              />
            </>
          )}

          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "relative",
              width: isMobile ? "100%" : "90%",
              flex: isMobile ? "0 0 50vh" : "1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 0,
              maxHeight: isMobile ? "50vh" : "none",
              overflow: "hidden",
              backgroundColor: darkTheme.palette.common.black,
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none"
            }}
          >
            <Box
              ref={imageContainerRef}
              sx={{
                width: "100%",
                height: "100%",
                maxWidth: "100%",
                maxHeight: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                padding: isMobile ? "16px" : 0,
                userSelect: "none",
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",
                pointerEvents: "auto"
              }}
            >
              <LightboxCloseButton onClose={onClose} isMobile={isMobile} />
              {showCounter && images.length > 1 && (
                <LightboxCounter
                  currentIndex={currentIndex}
                  totalImages={images.length}
                  isMobile={isMobile}
                  position={counterPosition}
                />
              )}
              {showRotateButton && (
                <LightboxRotateButton
                  onRotate={handleRotate}
                  isMobile={isMobile}
                />
              )}
              {showAdditionalInfoBadge && currentImage?.hasAdditionalInfo && (
                <AdditionalInfoBadge
                  createdAt={currentImage.createdAt}
                  isMobile={isMobile}
                  position="top-left"
                />
              )}
              {currentImage && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -20,
                    left: -20,
                    right: -20,
                    bottom: -20,
                    backgroundImage: `url(${currentImage.url})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "blur(20px) brightness(0.4)",
                    zIndex: 1,
                    opacity: 0.8
                  }}
                />
              )}

              {images.length > 1 && isMobile && (
                <>
                  <CarouselArrow
                    direction="left"
                    onClick={handlePrevious}
                    isVisible={true}
                    sx={{ ...arrowSx, width: 33, height: 72, zIndex: 3 }}
                  />
                  <CarouselArrow
                    direction="right"
                    onClick={handleNext}
                    isVisible={true}
                    sx={{ ...arrowSx, width: 33, height: 72, zIndex: 3 }}
                  />
                </>
              )}

              {renderContent()}
            </Box>
          </Box>

          {showThumbnails && images.length > 1 && !isMobile && (
            <LightboxThumbnails
              images={images}
              currentIndex={currentIndex}
              onThumbnailClick={onIndexChange}
              isMobile={isMobile}
            />
          )}
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default UnifiedLightbox;
